import './connections-premium.css';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { Alert, Box, Chip, Container, Paper, Stack, Typography } from '@mui/material';
import GlobalConnectionStatusHero from './GlobalConnectionStatusHero';
import ConnectedExchangesSection from './ConnectedExchangesSection';
import OnChainWalletsSection from './OnChainWalletsSection';
import AddConnectionSection from './AddConnectionSection';
import SecurityInfoSection from './SecurityInfoSection';
import LocalEncryptionInfoSection from './LocalEncryptionInfoSection';
import React, { useCallback, useEffect, useRef, useState } from 'react';

function getUserIdFromAuth() {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return user && user.login ? user.login : undefined;
  } catch {
    return undefined;
  }
}

const ConnectionsPage = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncAllRequestId, setSyncAllRequestId] = useState(0);
  const [syncAllPendingScopes, setSyncAllPendingScopes] = useState([]);
  const syncAllActiveRequestRef = useRef(0);
  const userId = getUserIdFromAuth();

  const handleSyncAll = useCallback(() => {
    setSyncAllRequestId((value) => {
      const nextRequestId = value + 1;
      syncAllActiveRequestRef.current = nextRequestId;
      setSyncAllPendingScopes(['exchanges', 'wallets']);
      return nextRequestId;
    });
  }, []);

  const handleSyncAllComplete = useCallback((scope, requestId) => {
    if (requestId && requestId !== syncAllActiveRequestRef.current) {
      return;
    }
    setSyncAllPendingScopes((current) => current.filter((entry) => entry !== scope));
  }, []);

  useEffect(() => {
    if (syncAllPendingScopes.length === 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setSyncAllPendingScopes([]);
    }, 45000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [syncAllPendingScopes]);

  const syncAllLoading = syncAllPendingScopes.length > 0;

  return (
    <Box
      sx={{
        minHeight: '100%',
        background: 'radial-gradient(circle at top left, rgba(228,197,114,0.18) 0%, rgba(228,197,114,0) 28%), linear-gradient(180deg, #f8f4ec 0%, #f2ede3 52%, #ebe5db 100%)',
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 6,
            p: { xs: 3, md: 5 },
            mb: 4,
            background: 'linear-gradient(135deg, #10252b 0%, #214550 38%, #43736f 100%)',
            color: '#f8f4ec',
          }}
        >
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} justifyContent="space-between" alignItems="stretch">
            <Box sx={{ maxWidth: 760 }}>
              <Chip
                label="Connections Control Center"
                sx={{ mb: 2, bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', letterSpacing: '0.08em', fontWeight: 700 }}
              />
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1.5, maxWidth: 720 }}>
                Connexions des exchanges et wallets
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
                <Chip
                  label={userId ? `Utilisateur: ${userId}` : 'Aucun utilisateur connecté'}
                  sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }}
                />
                <Chip
                  label="Clés API chiffrées côté backend"
                  sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }}
                />
                <Chip
                  label="Rafraîchissement coordonné des sections"
                  sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }}
                />
              </Stack>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 5,
                background: 'rgba(248,244,236,0.08)',
                border: '1px solid rgba(248,244,236,0.14)',
                backdropFilter: 'blur(10px)',
                minWidth: { xl: 360 },
              }}
            >
              <Stack spacing={2.1}>
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <HubRoundedIcon sx={{ mt: 0.2, color: '#f3c65c' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8f4ec' }}>
                      Exchanges centralisés
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.7 }}>
                      Ajout, suppression et synchronisation des connexions exchange depuis une zone dédiée.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <AccountBalanceWalletRoundedIcon sx={{ mt: 0.2, color: '#8fd6b1' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8f4ec' }}>
                      Wallets on-chain
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.7 }}>
                      Vue séparée pour les adresses, la synchronisation native et les tokens personnalisés.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <SyncRoundedIcon sx={{ mt: 0.2, color: '#f8f4ec' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8f4ec' }}>
                      Statut global cohérent
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.7 }}>
                      Le hero, la liste des exchanges et les wallets se remettent à jour ensemble après ajout ou suppression.
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Paper>

        {!userId ? (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
            Aucun utilisateur connecté. L’affichage reste accessible, mais la création de connexions dépendra du contexte d’authentification courant.
          </Alert>
        ) : null}

        <div className="connections-page">
          <div className="connections-grid connections-grid--top">
            <GlobalConnectionStatusHero
              key={`hero-${refreshKey}`}
              onSyncAll={handleSyncAll}
              syncAllLoading={syncAllLoading}
            />
            <AddConnectionSection onConnectionAdded={() => setRefreshKey((value) => value + 1)} userId={userId} />
          </div>

          <ConnectedExchangesSection
            key={`exchanges-${refreshKey}`}
            userId={userId}
            syncAllRequestId={syncAllRequestId}
            onSyncAllComplete={handleSyncAllComplete}
          />
          <OnChainWalletsSection
            key={`wallets-${refreshKey}`}
            userId={userId}
            syncAllRequestId={syncAllRequestId}
            onSyncAllComplete={handleSyncAllComplete}
          />

          <div className="connections-grid connections-grid--support">
            <SecurityInfoSection />
            <LocalEncryptionInfoSection />
          </div>
        </div>
      </Container>
    </Box>
  );
};

export default ConnectionsPage;


